// ─────────────────────────────────────────────────────────────────────────────
// Subtitles the TEACHER supplies, in the language of the video.
//
// WHY NOT YOUTUBE'S OWN CAPTIONS. The Gibberfish app reads captions out of the
// player by injecting script into its WebView (see app/watch.tsx and the
// youtube-caption-lockdown memory). A web page cannot: the embed is a
// cross-origin iframe, and fetching captions from a server is blocked by
// YouTube (PO tokens; datacenter IPs worst of all). So the teacher pastes the
// transcript once, and every student gets it.
//
// Accepted:
//   • YouTube's "Show transcript" panel, copied — timestamps on their own line
//     ("0:04" then the text) or inline ("0:04 hola a todos"), with or without
//     the screen-reader lines YouTube interleaves ("4 seconds",
//     "1 minute, 4 seconds").
//   • SRT and WebVTT files.
// ─────────────────────────────────────────────────────────────────────────────

/** One subtitle line: start and end in seconds, and its text. */
export type Cue = { s: number; e: number; t: string };

const MAX_CUE_SECONDS = 8;
const LAST_CUE_SECONDS = 5;

function hmsToSeconds(h: string | undefined, m: string, s: string, ms?: string): number {
  return (Number(h ?? 0) * 3600) + Number(m) * 60 + Number(s) + (ms ? Number(`0.${ms}`) : 0);
}

/** "1:02:03", "12:34", "0:04" → seconds. */
function clockToSeconds(stamp: string): number | null {
  const m = stamp.match(/^(?:(\d{1,2}):)?(\d{1,2}):(\d{2})$/);
  return m ? hmsToSeconds(m[1], m[2], m[3]) : null;
}

const clean = (s: string) => s
  .replace(/<[^>]+>/g, '')            // VTT styling tags
  .replace(/\{\\[^}]+\}/g, '')        // SRT/ASS override tags
  .replace(/\s+/g, ' ')
  .trim();

function parseTimed(text: string): Cue[] {
  // SRT / VTT: "00:00:01,000 --> 00:00:04,000" (VTT may omit hours, uses '.').
  const arrow = /(?:(\d{1,2}):)?(\d{1,2}):(\d{2})[.,](\d{1,3})\s*-->\s*(?:(\d{1,2}):)?(\d{1,2}):(\d{2})[.,](\d{1,3})/;
  const cues: Cue[] = [];
  for (const block of text.replace(/\r\n/g, '\n').split(/\n\s*\n/)) {
    const lines = block.split('\n');
    const i = lines.findIndex(l => arrow.test(l));
    if (i < 0) continue;
    const m = lines[i].match(arrow)!;
    const t = clean(lines.slice(i + 1).join(' '));
    if (!t) continue;
    cues.push({
      s: hmsToSeconds(m[1], m[2], m[3], m[4]),
      e: hmsToSeconds(m[5], m[6], m[7], m[8]),
      t,
    });
  }
  return cues;
}

/** The screen-reader lines YouTube puts under each timestamp when copied. */
const SPOKEN_DURATION = /^(\d+\s+hours?,?\s*)?(\d+\s+minutes?,?\s*)?(\d+\s+seconds?)?$/i;

function parseTranscriptPanel(text: string): Cue[] {
  const starts: { s: number; parts: string[] }[] = [];
  for (const raw of text.replace(/\r\n/g, '\n').split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    const inline = line.match(/^((?:\d{1,2}:)?\d{1,2}:\d{2})\s+(.+)$/);
    const alone = clockToSeconds(line);
    if (alone !== null) { starts.push({ s: alone, parts: [] }); continue; }
    if (inline) {
      const s = clockToSeconds(inline[1]);
      if (s !== null) { starts.push({ s, parts: [inline[2]] }); continue; }
    }
    if (SPOKEN_DURATION.test(line)) continue;
    starts[starts.length - 1]?.parts.push(line);
  }

  const cues: Cue[] = [];
  starts.forEach((x, i) => {
    const t = clean(x.parts.join(' '));
    if (!t) return;
    const next = starts[i + 1]?.s;
    const e = next !== undefined && next > x.s
      ? Math.min(next, x.s + MAX_CUE_SECONDS)
      : x.s + LAST_CUE_SECONDS;
    cues.push({ s: x.s, e, t });
  });
  return cues;
}

/** Whatever was pasted, as sorted cues. Empty if nothing timed was found. */
export function parseSubtitles(text: string): Cue[] {
  const cues = /-->/.test(text) ? parseTimed(text) : parseTranscriptPanel(text);
  return cues
    .filter(c => Number.isFinite(c.s) && c.e > c.s && c.t)
    .sort((a, b) => a.s - b.s)
    .slice(0, 5000)
    .map(c => ({ s: Math.round(c.s * 100) / 100, e: Math.round(c.e * 100) / 100, t: c.t.slice(0, 500) }));
}

/** Index of the cue on screen at `t`, or -1 between cues. */
export function cueIndexAt(cues: Cue[], t: number): number {
  let lo = 0;
  let hi = cues.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (cues[mid].s <= t) { found = mid; lo = mid + 1; } else hi = mid - 1;
  }
  return found >= 0 && t < cues[found].e ? found : -1;
}

/** The last cue that STARTED at or before `t` — for "replay this line". */
export function lastCueBefore(cues: Cue[], t: number): number {
  let lo = 0;
  let hi = cues.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (cues[mid].s <= t) { found = mid; lo = mid + 1; } else hi = mid - 1;
  }
  return found;
}
